/**
 * ConfigurationStatus
 *
 * Shows a welcome/info card if nothing is configured yet
 * and (optionally) an error alert.
 */
import React from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

/**
 * ConfigurationStatus Component
 *
 * Displays configuration status alerts and error messages.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.notConfigured - Whether configuration is not set up
 * @param {string} props.error - Error message to display
 * @param {boolean} props.showError - Whether to show error alert
 *
 * @component
 */
export default function ConfigurationStatus({
  notConfigured,
  error,
  showError,
}) {
  return (
    <>
      {notConfigured && (
        <Card
          elevation={4}
          sx={{
            mb: 3,
            border: "2px solid",
            borderColor: "info.main",
            borderRadius: 3,
            background: "linear-gradient(135deg, #e3f2fd 0%, #f8fcff 100%)",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(33, 150, 243, 0.15)",
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <InfoIcon
                color="info"
                sx={{
                  fontSize: 32,
                  mt: 0.5,
                  filter: "drop-shadow(0 2px 4px rgba(33, 150, 243, 0.2))",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "info.dark",
                    mb: 1,
                    fontSize: "1.2rem",
                  }}
                >
                  Welcome to Slideshow Configuration
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "info.dark",
                    lineHeight: 1.6,
                    opacity: 0.9,
                  }}
                >
                  It looks like you haven't set up the slideshow settings for
                  this community yet.
                  <br />
                  Please configure your slides below and save your configuration
                  to get started.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
      {showError && error && false && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );
}
